import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import VerifiedIcon from '@mui/icons-material/Verified';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { translate } from 'src/i18n/tHelper';
import { usePatolojiEImzaliKayitBilgisiQuery } from 'src/services/PatolojiModul/PatolojiLaboratuvarIslemleriApi';

const translatePath = "pages|patoloji|header";

function formatNumberShort(num: number): string {
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    return num.toString();
}

const ImzaliDosyalarSayaci = (): JSX.Element => {
    const query = usePatolojiEImzaliKayitBilgisiQuery(null, { pollingInterval: 30000 });

    const toplamKayit = query.data?.toplamKayit || 0;
    const imzaliKayit = query.data?.imzaliKayit || 0;
    const bekleyenKayit = toplamKayit - imzaliKayit;
    const yuzde = toplamKayit > 0 ? Math.round((imzaliKayit / toplamKayit) * 100) : 0;

    const getStatusConfig = () => {
        if (yuzde >= 90) {
            return {
                color: '#10b981',
                bgColor: '#ecfdf5',
                borderColor: '#a7f3d0',
                status: 'excellent'
            };
        } else if (yuzde >= 75) {
            return {
                color: '#059669',
                bgColor: '#f0fdf4',
                borderColor: '#bbf7d0',
                status: 'good'
            };
        } else if (yuzde >= 50) {
            return {
                color: '#f59e0b',
                bgColor: '#fffbeb',
                borderColor: '#fed7aa',
                status: 'warning'
            };
        } else {
            return {
                color: '#dc2626',
                bgColor: '#fef2f2',
                borderColor: '#fecaca',
                status: 'critical'
            };
        }
    };

    const statusConfig = getStatusConfig();

    const TooltipContent = () => (
        <Paper sx={{
            p: 2,
            minWidth: 280,
            bgcolor: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid #e5e7eb'
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <VerifiedIcon sx={{ color: statusConfig.color, fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827' }}>
                    {translate(translatePath, "E-imzalı durum raporu")}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress
                        variant="determinate"
                        value={100}
                        size={120}
                        thickness={8}
                        sx={{ color: '#f3f4f6' }}
                    />
                    <CircularProgress
                        variant="determinate"
                        value={yuzde}
                        size={120}
                        thickness={8}
                        sx={{
                            color: statusConfig.color,
                            position: 'absolute',
                            left: 0,
                            '& .MuiCircularProgress-circle': {
                                strokeLinecap: 'round',
                            },
                        }}
                    />
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <Typography variant="h4" sx={{
                            fontWeight: 800,
                            color: statusConfig.color,
                            lineHeight: 1
                        }}>
                            {yuzde}%
                        </Typography>
                        <Typography variant="caption" sx={{
                            color: '#6b7280',
                            fontWeight: 500
                        }}>
                            {translate(translatePath, "Tamamlandı")}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ space: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: statusConfig.color
                        }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                            {translate(translatePath, "İmzalı Dosyalar")}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{
                        fontWeight: 700,
                        color: statusConfig.color
                    }}>
                        {imzaliKayit.toLocaleString()}
                    </Typography>
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={yuzde}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: statusConfig.color + '15',
                        '& .MuiLinearProgress-bar': {
                            bgcolor: statusConfig.color,
                            borderRadius: 3
                        }
                    }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, mt: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#f59e0b'
                        }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                            {translate(translatePath, "Bekleyen Dosyalar")}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{
                        fontWeight: 700,
                        color: '#f59e0b'
                    }}>
                        {bekleyenKayit.toLocaleString()}
                    </Typography>
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={bekleyenKayit > 0 ? (bekleyenKayit / toplamKayit) * 100 : 0}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#fef3c7',
                        '& .MuiLinearProgress-bar': {
                            bgcolor: '#f59e0b',
                            borderRadius: 3
                        }
                    }}
                />
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 2,
                    pt: 1.5,
                    borderTop: '1px solid #f3f4f6'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon sx={{ color: '#6b7280', fontSize: 16 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                            {translate(translatePath, "Toplam Dosya")}
                        </Typography>
                    </Box>
                    <Typography variant="h6" sx={{
                        fontWeight: 800,
                        color: '#111827'
                    }}>
                        {toplamKayit.toLocaleString()}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: statusConfig.bgColor,
                border: `1px solid ${statusConfig.borderColor}`
            }}>
                <Typography variant="caption" sx={{
                    fontWeight: 600,
                    color: statusConfig.color,
                    textAlign: 'center',
                    display: 'block'
                }}>
                    {yuzde >= 90 ? (
                        <>
                            🎉 {translate(translatePath, 'Mükemmel! Dosyaların büyük kısmı imzalandı.')}
                        </>
                    ) :
                        yuzde >= 75 ? (
                            <>
                                👍 {translate(translatePath, 'İyi durumda! Çoğu dosya imzalandı.')}
                            </>
                        ) :
                            yuzde >= 50 ? (
                                <>
                                    ⚠️ {translate(translatePath, 'Orta seviye. Daha fazla imza gerekli.')}
                                </>
                            ) : (
                                <>
                                    🚨 {translate(translatePath, 'Dikkat! Çok fazla imzasız dosya var.')}
                                </>
                            )}
                </Typography>
            </Box>
        </Paper>
    );

    if (query.isLoading || query.isFetching) {
        return (
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: 'rgba(0,0,0,0.02)'
            }}>
                <CircularProgress size={16} sx={{ color: '#6b7280' }} />
                <Typography variant="caption" sx={{ ml: 1, color: '#6b7280' }}>
                    {translate(translatePath, "Yükleniyor...")}
                </Typography>
            </Box>
        );
    }

    return (
        <Tooltip
            title={<TooltipContent />}
            arrow
            placement="bottom-start"
            PopperProps={{
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 10],
                        },
                    },
                ],
            }}
        >
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0,
                borderRadius: '4px',
                bgcolor: statusConfig.bgColor,
                border: `1px solid ${statusConfig.borderColor}`,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                height: 28,
                minHeight: 28,
                minWidth: 'fit-content',
                margin: 0,
                marginLeft: '4px',
                '&:hover': {
                    bgcolor: statusConfig.color + '10',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                    transform: 'translateY(-1px)',
                    borderColor: statusConfig.color + '40'
                },
                '&:active': {
                    transform: 'translateY(0)'
                }
            }}>
                <Box sx={{ position: 'relative', display: 'flex' }}>
                    <CircularProgress
                        variant="determinate"
                        value={yuzde}
                        size={16}
                        thickness={4}
                        sx={{
                            color: statusConfig.color,
                            '& .MuiCircularProgress-circle': {
                                strokeLinecap: 'round'
                            }
                        }}
                    />
                    <CircularProgress
                        variant="determinate"
                        value={100}
                        size={16}
                        thickness={4}
                        sx={{
                            color: statusConfig.borderColor,
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: -1
                        }}
                    />
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <VerifiedIcon
                            sx={{
                                fontSize: 8,
                                color: statusConfig.color,
                                opacity: yuzde > 0 ? 1 : 0.3
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: '#374151',
                                fontSize: '0.6rem',
                                lineHeight: 1
                            }}
                        >
                            {translate(translatePath, "E-İmza")}
                        </Typography>
                        <Chip
                            label={`${yuzde}%`}
                            size="small"
                            sx={{
                                height: 12,
                                fontSize: '0.55rem',
                                fontWeight: 700,
                                bgcolor: statusConfig.color + '15',
                                color: statusConfig.color,
                                border: 'none',
                                '& .MuiChip-label': {
                                    px: 0.4
                                }
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.2 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                color: statusConfig.color,
                                fontSize: '0.65rem',
                                lineHeight: 1
                            }}
                        >
                            {formatNumberShort(imzaliKayit)}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#9ca3af',
                                fontSize: '0.55rem'
                            }}
                        >
                            /{formatNumberShort(toplamKayit)}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: statusConfig.color, boxShadow: `0 0 0 1px ${statusConfig.color}20` }} />
            </Box>
        </Tooltip>
    );
};

export default ImzaliDosyalarSayaci;